import type { KeyboardEvent, MouseEvent } from 'react';

import { FormFieldChoiceLabel } from '@components/core-ui/form-elements/FormFieldChoiceLabel';
import { FormFieldDisabledGroup } from '@components/core-ui/form-elements/FormFieldDisabledGroup';
import { TProps } from './types/TProps';

const switchTrackRootClassName = 'switch-track';

const switchTrackClassName =
  'relative inline-block h-[15px] w-[25px] shrink-0 grow-0 rounded-[10px] text-center transition-colors duration-200';

const switchTrackCheckedClassName = 'bg-[#cadcf8]';

const switchTrackUncheckedClassName = 'bg-[#d7d7d7]';

const switchTrackDisabledClassName = 'bg-[var(--color-form-field-disabled-bg,var(--color-border))]';

const switchPointClassName =
  'point absolute top-px h-[13px] w-[13px] rounded-[10px] transition-[left,background-color] duration-200';

const switchPointCheckedClassName = 'left-[11px]';

const switchPointUncheckedClassName = 'left-px';

const switchPointCheckedColorClassName = 'bg-[#858DBD]';

const switchPointUncheckedColorClassName = 'bg-[var(--color-surface)]';

const switchPointDisabledColorClassName = 'bg-[var(--color-form-field-disabled-icon,var(--color-border))]';

export const Switch = ({
  onChange,
  checked = false,
  disabled = false,
  className = 'switch',
  testId,
  label,
  children,
}: TProps) => {
  const labelContent = label ?? children;
  const hasLabel = labelContent !== undefined && labelContent !== null && labelContent !== false;

  const activate = () => {
    if (disabled) {
      return;
    }
    onChange();
  };

  const handlePointerDown = (event: MouseEvent<HTMLLabelElement>) => {
    event.stopPropagation();
  };

  const handleClick = (event: MouseEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
    activate();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLLabelElement>) => {
    if (disabled) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      activate();
    }
  };

  return (
    <FormFieldDisabledGroup disabled={disabled}>
      <label
        className={[
          'relative inline-flex select-none items-center',
          disabled ? '' : 'cursor-pointer [&_svg]:cursor-pointer',
          hasLabel ? 'gap-2' : '',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        onMouseDown={handlePointerDown}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="switch"
        aria-checked={checked}
        data-testid={testId}
      >
        <span
          className={[
            switchTrackRootClassName,
            switchTrackClassName,
            disabled
              ? switchTrackDisabledClassName
              : checked
                ? switchTrackCheckedClassName
                : switchTrackUncheckedClassName,
          ].join(' ')}
        >
          <span
            className={[
              switchPointClassName,
              checked ? switchPointCheckedClassName : switchPointUncheckedClassName,
              disabled
                ? switchPointDisabledColorClassName
                : checked
                  ? switchPointCheckedColorClassName
                  : switchPointUncheckedColorClassName,
            ].join(' ')}
          />
        </span>
        {hasLabel ? <FormFieldChoiceLabel disabled={disabled}>{labelContent}</FormFieldChoiceLabel> : null}
      </label>
      <input type="checkbox" role="switch" checked={checked} readOnly className="hidden" tabIndex={-1} aria-hidden />
    </FormFieldDisabledGroup>
  );
};
